// 本文更新（object.text.update）を、文字数上限と送信サイズ上限の両方へ収めるための調整。
//
// 文字数だけを見ても送信サイズ上限（websocket-realtime の CLIENT_MESSAGE_MAX_BYTES）には掛かり得る
// （本文が長く、かつ部分書式の run が多い場合）。実際に送信を拒否するのは送信直前の
// `JSON.stringify` 実測であり、固定の予約バイト数による見積りとは一致しない。
// また `rebaseTextInlineStyles` は本文差分を override マップへ写してから run を再収集するため、
// 本文を短くしても run の件数が単調に減るとは限らない。
// そこで、ペンストローク確定（pen-stroke-payload）と同じ「正規化 → 実測 → 収まるまで削る」形を採り、
// 実測対象は「実際に送る op.apply メッセージそのもの」とする。
//
// 通常の入力・貼り付けでは、安価な事前判定だけで通過させ、実測は行わない。

import {
  TEXT_INLINE_STYLE_RUNS_MAX,
  clampTextBodyAtCaret,
  jsonStringByteLength,
  normalizeCaretToCodePointBoundary,
  type TextBodyClampReason,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";

/**
 * object.text.update の op.apply メッセージ全体に許すバイト数。
 * 送信メッセージ全体の上限 30,720 バイト（websocket-realtime の CLIENT_MESSAGE_MAX_BYTES）
 * から 2 KB を残す。この余裕は、実測に使う clientOpId / clientCreatedAt と実際の送信値との
 * 桁揺れ、および将来 op のフィールドが増えた場合の緩衝に充てる。
 * 実際の関係（本値 < CLIENT_MESSAGE_MAX_BYTES）は単体テストで固定する。
 */
export const TEXT_UPDATE_MESSAGE_MAX_BYTES = 28 * 1024;

/** 「正規化 → 実測 → 超過分だけ追加で切り詰め」の反復上限。通常は実測 1 回で収束する。 */
export const TEXT_BODY_FIT_MAX_ATTEMPTS = 3;

/** 上記で収束しなかった場合に、予算を半分ずつにして後退する試行の上限。 */
export const TEXT_BODY_FALLBACK_MAX_ATTEMPTS = 4;

/** 部分書式 run 1 件の直列化バイト数の上界（実測の最長形は約 98 バイト）。 */
export const TEXT_INLINE_STYLE_RUN_MAX_BYTES = 128;

/** 空本文で実測した封筒に対して、数値フィールドの桁の伸びしろとして足す余裕。 */
export const TEXT_UPDATE_PRECHECK_MARGIN_BYTES = 256;

export interface TextBodyFitResult {
  /** 上限へ収めた本文。 */
  readonly text: string;
  /** 収めた結果のキャレット位置。 */
  readonly caret: number;
  /**
   * 収めた本文に対応する部分書式。部分書式を送らない対象では `undefined`。
   * 呼び出し側はこの値をそのまま DOM 構築・runs の保持・送信へ使う（自分で rebase し直さない）。
   */
  readonly inlineStyles: readonly TextInlineStyleRange[] | undefined;
  /** 入りきらずに切り詰めた文字数（表示用。コードポイント数）。 */
  readonly droppedCount: number;
  /** どの上限で切り詰めたか（反復全体の集約結果）。 */
  readonly reason: TextBodyClampReason;
  /**
   * この結果を送信してよいか。実測を尽くしても送信サイズ上限へ収められず、
   * 変更前の本文へ後退した場合だけ `false` になる。
   * `false` のとき、呼び出し側は送信を行わない（送っても送信サイズ上限で拒否されるだけであり、
   * サーバ側の本文を手つかずのまま残すほうが安全なため）。
   */
  readonly shouldSend: boolean;
}

export interface TextBodyFitInput {
  /** 変更前の本文（後退時にそのまま採用する）。 */
  readonly prevText: string;
  /** 変更後の本文。 */
  readonly nextText: string;
  /** 変更後のキャレット位置（＝いま挿入した部分の末尾）。 */
  readonly caret: number;
  /** 本文の文字数上限。 */
  readonly maxLength: number;
  /** 空本文・run 無しで実測した op.apply メッセージのバイト数。 */
  readonly envelopeBytes: number;
  /** 部分書式を op へ載せる対象か（付箋・セクション名では false）。 */
  readonly sendsInlineStyles: boolean;
  /** `prevText` に対応する現在の run。部分書式を送らない対象では `undefined`。 */
  readonly currentRuns: readonly TextInlineStyleRange[] | undefined;
  /** `prevText` からの本文変更へ run を追従させる純粋関数（フックの状態は更新しない）。 */
  readonly rebaseRuns: (nextText: string) => readonly TextInlineStyleRange[];
  /** その本文・run で送る op.apply メッセージの UTF-8 バイト数を実測する。 */
  readonly measureBytes: (
    text: string,
    runs: readonly TextInlineStyleRange[] | undefined
  ) => number;
}

/**
 * 事前判定に使う run 件数の上界。
 *
 * **切り詰め前の件数を使ってはいけない。** 本文を短くすると行構造が変わって run が切り直され、
 * 件数が増えることがあるためである。run が 0 件のときに 0 を返してよいのは、
 * `rebaseTextInlineStyles` が `runs.length === 0` で即座に空配列を返すためで、
 * 付箋・セクション名と書式を付けていないテキスト図形はすべてここに入る。
 *
 * それ以外では保存できる run の絶対上限（`TEXT_INLINE_STYLE_RUNS_MAX`）を使うが、
 * 正規形の run は非重複で `start < end` のため、件数が本文長を超えることはない。
 * 短い本文ではそちらのほうが厳しい上界になるので、小さい方を採る。
 */
function runsCountUpperBound(
  sendsInlineStyles: boolean,
  currentRunsCount: number,
  textLength: number
): number {
  if (!sendsInlineStyles || currentRunsCount === 0) return 0;
  return Math.min(TEXT_INLINE_STYLE_RUNS_MAX, textLength);
}

/**
 * 実測を省略してよいか判断するための安価な事前判定。
 * ここで使う係数は「実測を省略してよいか」の判断にのみ使い、上限内であることの根拠には使わない
 * （上限内であることは、実測と、収まらない場合の「送らない」への後退が保証する）。
 */
export function estimateTextUpdateMessageBytes(input: {
  readonly text: string;
  readonly envelopeBytes: number;
  readonly sendsInlineStyles: boolean;
  readonly currentRunsCount: number;
}): number {
  return (
    input.envelopeBytes +
    TEXT_UPDATE_PRECHECK_MARGIN_BYTES +
    jsonStringByteLength(input.text) +
    TEXT_INLINE_STYLE_RUN_MAX_BYTES *
      runsCountUpperBound(input.sendsInlineStyles, input.currentRunsCount, input.text.length)
  );
}

/**
 * 変更後の本文とキャレットを、文字数上限と送信サイズ上限の両方へ収める。
 *
 * 切り詰めはキャレット直前からしか行わず、反復のたびにキャレットは左へしか動かないため、
 * 削られた区間は常に `[最終キャレット, 正規化後の入力キャレット)` である。
 * 表示用の件数はこの区間のコードポイント数で数えるので、選択範囲をより短い文字列で置換する
 * 貼り付けや、本文が短くなる履歴復元でも負にならない。
 */
export function fitTextBodyForSend(input: TextBodyFitInput): TextBodyFitResult {
  const { maxLength, measureBytes, nextText, prevText, rebaseRuns, sendsInlineStyles } = input;
  const normalizedCaret = normalizeCaretToCodePointBoundary(nextText, input.caret);
  const currentRunsCount = input.currentRuns?.length ?? 0;

  // 1. まず文字数上限へ収める。通常の入力・貼り付けはここで切り詰め 0 件のまま通る。
  const clamped = clampTextBodyAtCaret({ caret: normalizedCaret, maxLength, text: nextText });
  let text = clamped.text;
  let caret = clamped.caret;
  let clampedByBytes = false;

  // 2. run の追従は常に行う（省略すると、事前判定を通った通常入力で本文と run がずれる）。
  //    事前判定が省略するのは実測だけである。
  let runs = sendsInlineStyles ? rebaseRuns(text) : undefined;

  const finish = (shouldSend: boolean): TextBodyFitResult => {
    if (!shouldSend) {
      return {
        caret: prevText.length,
        droppedCount: 0,
        inlineStyles: input.currentRuns,
        reason: "none",
        shouldSend: false,
        text: prevText,
      };
    }
    // 切り詰めはキャレット直前からしか行わないので、通常はキャレットの移動量が削った区間になる。
    // 保存済み本文自体が上限超過という異常系だけはキャレットより後ろも削るため、
    // 「削除しか行っていない」ことから常に非負である長さの差も見て、大きい方を採る。
    // 切り詰めが起きていない通常の入力では、どちらの数え方もコードポイント列を作らずに 0 で抜ける。
    const caretBased =
      caret === normalizedCaret ? 0 : [...nextText.slice(caret, normalizedCaret)].length;
    const lengthBased =
      nextText.length === text.length ? 0 : [...nextText].length - [...text].length;
    const droppedCount = Math.max(caretBased, lengthBased);
    const reason: TextBodyClampReason =
      droppedCount === 0 ? "none" : clampedByBytes ? "bytes" : clamped.reason;
    return { caret, droppedCount, inlineStyles: runs, reason, shouldSend: true, text };
  };

  // 3. 安価な事前判定を通れば、確実に収まるので実測しない。
  const estimated = estimateTextUpdateMessageBytes({
    currentRunsCount,
    envelopeBytes: input.envelopeBytes,
    sendsInlineStyles,
    text,
  });
  if (estimated <= TEXT_UPDATE_MESSAGE_MAX_BYTES) return finish(true);

  // 4〜5. 実測し、超過している間は「超過ぶんだけ本文予算を減らす」ことを繰り返す。
  //       本文は毎回必ず短くなるため反復は前進する。
  for (let attempt = 0; attempt < TEXT_BODY_FIT_MAX_ATTEMPTS; attempt += 1) {
    const measured = measureBytes(text, runs);
    if (measured <= TEXT_UPDATE_MESSAGE_MAX_BYTES) return finish(true);
    const bodyBytes = jsonStringByteLength(text);
    const budget = bodyBytes - (measured - TEXT_UPDATE_MESSAGE_MAX_BYTES);
    const next = clampTextBodyAtCaret({ caret, maxBytes: Math.max(0, budget), maxLength, text });
    if (next.droppedLength > 0) clampedByBytes = true;
    text = next.text;
    caret = next.caret;
    runs = sendsInlineStyles ? rebaseRuns(text) : undefined;
  }

  // 6. 収束しない場合は、予算を半分ずつにして後退し、そのつど実測し直す。
  for (let attempt = 0; attempt < TEXT_BODY_FALLBACK_MAX_ATTEMPTS; attempt += 1) {
    const budget = Math.floor(jsonStringByteLength(text) / 2);
    const next = clampTextBodyAtCaret({ caret, maxBytes: budget, maxLength, text });
    if (next.droppedLength > 0) clampedByBytes = true;
    text = next.text;
    caret = next.caret;
    runs = sendsInlineStyles ? rebaseRuns(text) : undefined;
    if (measureBytes(text, runs) <= TEXT_UPDATE_MESSAGE_MAX_BYTES) return finish(true);
  }

  // 7. それでも収まらなければ、その操作をまるごと諦めて送信しない。
  //    送ってしまうと結局 code: "limit" で拒否されてロールバックするため、
  //    送らずにサーバ側の本文を手つかずのまま残すほうが安全である。
  return finish(false);
}

/** 切り詰め・保存不可を利用者へ伝えるお知らせ（レベルと文面）。 */
export interface TextBodyFitNotice {
  readonly level: "info" | "warning";
  readonly message: string;
}

/**
 * 本文の文字数上限に達して切り詰めたときのお知らせ。
 * `droppedCount` は保存本文（生 Markdown）上のコードポイント数で、画面の見た目の文字数とは
 * 一致しないことがある（リンク付き HTML の貼り付けは `[表示テキスト](URL)` へ展開されるため）。
 */
export function textLengthClampNotice(droppedCount: number, maxLength: number): TextBodyFitNotice {
  return {
    level: "info",
    message: `本文の文字数上限（${maxLength.toLocaleString()} 文字）に達したため、入りきらない ${droppedCount.toLocaleString()} 文字を切り詰めました。`,
  };
}

/** 送信サイズ上限に達して切り詰めたときのお知らせ。 */
export function textBytesClampNotice(droppedCount: number): TextBodyFitNotice {
  return {
    level: "info",
    message: `1 回に送信できるデータ量の上限に達したため、入りきらない ${droppedCount.toLocaleString()} 文字を切り詰めました。文字の書式を減らすか、本文を分けてお試しください。`,
  };
}

/** 送信サイズ上限へ収められず、その変更を保存できなかったときのお知らせ。 */
export function textNotSentNotice(): TextBodyFitNotice {
  return {
    level: "warning",
    message:
      "本文が大きすぎるため、この変更を保存できませんでした。文字の書式を減らすか、本文を分けてお試しください。",
  };
}

/**
 * 切り詰め結果に対応するお知らせを返す。出す必要がなければ `null`。
 * `reason: "none"`（切り詰めなし）では、通常の貼り付けで通知が出ないよう何も返さない。
 */
export function textBodyFitNotice(
  result: Pick<TextBodyFitResult, "droppedCount" | "reason" | "shouldSend">,
  maxLength: number
): TextBodyFitNotice | null {
  if (!result.shouldSend) return textNotSentNotice();
  if (result.droppedCount <= 0 || result.reason === "none") return null;
  return result.reason === "bytes"
    ? textBytesClampNotice(result.droppedCount)
    : textLengthClampNotice(result.droppedCount, maxLength);
}
